import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

import { EXTERNAL_ICONS, APP_STATES } from "shared/enum";

type InitialLoadingProps = {
  error: boolean;
  progress: number;
  dataLoaded: boolean;
  dataLoadError: boolean;
  loadingState: APP_STATES;
  handleLoadingClick: () => void;
};

export default function InitialLoading({
  error,
  progress,
  dataLoaded,
  dataLoadError,
  loadingState,
  handleLoadingClick,
}: InitialLoadingProps) {
  return (
    <Container>
      {!error && loadingState === APP_STATES.INITIAL_LOADING && (
        <ProgressText variant="h2">{progress}%</ProgressText>
      )}

      {!error && loadingState === APP_STATES.INITIAL_LOADING ? (
        <LoadingImagesContainer>
          <LoadingVehicleImage src={EXTERNAL_ICONS.LOADING_OVERLAY_VEHICLE_IMG} />
          <LoadingCircleImage src={EXTERNAL_ICONS.LOADING_OVERLAY_CIRCLE_IMG} />
        </LoadingImagesContainer>
      ) : (
        <IconAnimationContainer
          onClick={!dataLoadError && dataLoaded ? handleLoadingClick : () => {}}
        >
          <StartLoadingIcon
            src={EXTERNAL_ICONS.START_LOADING}
            dataLoaded={dataLoaded && !dataLoadError}
          />
        </IconAnimationContainer>
      )}

      <StatusText variant="h4">
        {!dataLoadError && !dataLoaded && "Loading..."}
        {(error || dataLoadError) && "Failed to load! Please try again."}
        {!error && dataLoaded && loadingState === APP_STATES.UNLOADED && "Click to visualize"}
        {!error &&
          dataLoaded &&
          loadingState === APP_STATES.INITIAL_LOADING &&
          `Loading your experience`}
      </StatusText>
    </Container>
  );
}

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  row-gap: 1.5rem;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: #000;
  z-index: 950;
`;

const GlowingAnimation = keyframes`
  0% {
    box-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #191919, 0 0 40px #191919, 0 0 50px #2c2c2c, 0 0 60px #2c2c2c, 0 0 70px #676467;
  }

  100% {
    box-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #676467, 0 0 40px #676467, 0 0 50px #676467, 0 0 60px #676467, 0 0 70px #676467;
  }
`;

const IconAnimationContainer = styled("div")`
  width: 100px;
  height: 100px;
  cursor: pointer;
  border-radius: 50%;
  background-color: #fff;
  animation: ${GlowingAnimation} 1s ease-in-out infinite alternate;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

type StartLoadingIconProps = {
  dataLoaded: boolean;
};

const StartLoadingIcon = styled("img")<StartLoadingIconProps>`
  cursor: pointer;
  filter: grayscale(${({ dataLoaded }) => (dataLoaded ? 0 : 1)});
  transition: filter 0.3s;
`;

const LoadingImagesContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  row-gap: 1.5rem;
`;

const LoadingCircleImage = styled("img")`
  width: 75px;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 50px;
  }
`;

const LoadingVehicleImage = styled("img")`
  width: 45%;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 100%;
  }
`;

const StatusText = styled(Typography)`
  opacity: 0.6;
  font-weight: 500;
  line-height: 1.167;
  letter-spacing: 0;
`;

const ProgressText = styled(Typography)`
  font-weight: 800;
  font-size: 120px;
  line-height: 1.167;
  letter-spacing: 0;
  color: #6a6a6a;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5) 0, #fff 60%);
  -webkit-text-fill-color: transparent;
  outline: 0;
  background-clip: text;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    font-size: 90px;
  }
`;
